/* eslint-disable lingui/no-unlocalized-strings */
// Shared legal-page chrome (privacy / terms / refund). Mirrors the layout of
// design/source/public/{privacy,terms,refund}.html: 260px sticky sidebar +
// numbered sections in a `<article>`.
import type { ReactNode } from "react";

export type LegalSection = {
  id: string;
  title: string;
  body: ReactNode;
};

type LegalLayoutProps = {
  /** Eyebrow above the page title — e.g. "Legal" */
  eyebrow?: string;
  /** Plain part of the page title */
  titleHead: string;
  /** Italic emphasis at the end of the title — render alongside titleHead */
  titleEmph?: string;
  /** "Last updated …" line under the title */
  meta: string;
  /** Optional summary callout shown above the section nav */
  summary?: { label: string; body: string };
  sections: readonly LegalSection[];
  /** Optional trailing block under the article body (refund page uses this) */
  trailing?: ReactNode;
};

export const LegalLayout = ({
  eyebrow = "Legal",
  titleHead,
  titleEmph,
  meta,
  summary,
  sections,
  trailing,
}: LegalLayoutProps) => (
  <section className="border-b border-border">
    <div className="mx-auto max-w-[1280px] px-6 py-20">
      <div className="grid grid-cols-1 gap-16 md:grid-cols-[260px_1fr]">
        <aside className="md:sticky md:top-24 md:self-start">
          <span className="text-xs font-medium text-muted-foreground">{eyebrow}</span>
          <h1 className="mt-3 font-serif text-[32px] font-semibold leading-[1.05] tracking-tight text-foreground">
            {titleHead}
            {titleEmph ? (
              <>
                {" "}
                <span className="italic">{titleEmph}</span>
              </>
            ) : null}
          </h1>
          <p className="mt-3 text-[12.5px] text-muted-foreground">{meta}</p>
          {summary ? (
            <div className="mt-6 rounded-xl border border-border bg-surface p-4">
              <div className="text-xs font-medium text-muted-foreground">{summary.label}</div>
              <p className="mt-2 text-[13px] leading-[1.55] text-foreground">{summary.body}</p>
            </div>
          ) : null}
          <nav className="mt-6 hidden md:block">
            <div className="text-xs font-medium text-muted-foreground">Sections</div>
            <ul className="mt-3 space-y-2 text-[13px]">
              {sections.map((section) => (
                <li key={section.id}>
                  <a
                    href={`#${section.id}`}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    {section.title}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </aside>
        <article className="space-y-12">
          {sections.map((section) => (
            <section key={section.id} id={section.id}>
              <h2 className="font-serif text-[22px] font-semibold tracking-tight text-foreground">
                {section.title}
              </h2>
              <div className="mt-4 space-y-3 text-[14.5px] leading-[1.7] text-foreground/85">
                {section.body}
              </div>
            </section>
          ))}
          {trailing}
        </article>
      </div>
    </div>
  </section>
);
